/**
 * a2ui core-18 control renderers (presentational) — the interactive + simple
 * leaf components the basic catalog defines that were not in our kit before:
 * AudioPlayer, CheckBox, ChoicePicker, DateTimeInput, Slider, TextField, Icon,
 * Divider. Props are the basic catalog verbatim; OUR design (tokens) applies.
 *
 * Pure/controlled: each takes a value + onChange so the session surface (a2ui
 * data model + follow_up) and the widget kit (viewIr data lane) can both drive
 * them. No data-model or interpreter coupling here.
 */
import { useId } from 'react';

const LABEL = { fontSize: 13, fontWeight: 600, color: 'var(--vi-text, #111)', marginBottom: 4, display: 'block' };
const FIELD = {
  width: '100%', boxSizing: 'border-box', font: 'inherit', fontSize: 15,
  padding: '8px 10px', borderRadius: 10, border: '1px solid var(--vi-hair, #E5E5EA)',
  background: 'var(--vi-surface, #fff)', color: 'var(--vi-text, #111)',
};

export function ViIcon({ name, size = 18, tone }) {
  const glyph = typeof name === 'string' ? name : (name && (name.svgPath || name.path) ? '●' : '•');
  return (
    <span data-a2ui-icon={typeof name === 'string' ? name : 'icon'} aria-hidden="true"
      style={{ fontSize: size, lineHeight: 1, color: tone ? `var(--vi-${tone}, currentColor)` : 'inherit' }}>
      {glyph}
    </span>
  );
}

export function ViDivider({ axis = 'horizontal' }) {
  const vertical = axis === 'vertical';
  return (
    <hr data-a2ui-divider={axis}
      style={vertical
        ? { width: 1, height: '100%', border: 0, background: 'var(--vi-hair, #E5E5EA)', margin: '0 8px' }
        : { height: 1, width: '100%', border: 0, background: 'var(--vi-hair, #E5E5EA)', margin: '8px 0' }} />
  );
}

export function ViAudioPlayer({ url, description }) {
  if (!url) return null;
  return (
    <figure data-a2ui-audio style={{ margin: 0 }}>
      <audio controls src={url} style={{ width: '100%' }}>
        <track kind="captions" />
      </audio>
      {description ? <figcaption style={{ fontSize: 12, color: 'var(--vi-text-muted, #8E8E93)', marginTop: 4 }}>{description}</figcaption> : null}
    </figure>
  );
}

export function ViCheckBox({ label, checked, onChange }) {
  const id = useId();
  return (
    <label htmlFor={id} data-a2ui-checkbox style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 15 }}>
      <input id={id} type="checkbox" checked={!!checked} onChange={(e) => onChange?.(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function ViSlider({ label, value, min = 0, max = 100, step = 1, onChange }) {
  const v = value == null ? min : Number(value);
  return (
    <div data-a2ui-slider>
      {label ? <span style={LABEL}>{label} <span style={{ fontWeight: 400, color: 'var(--vi-text-muted,#8E8E93)' }}>{v}</span></span> : null}
      <input type="range" min={min} max={max} step={step || 1} value={v}
        onChange={(e) => onChange?.(Number(e.target.value))} style={{ width: '100%' }} />
    </div>
  );
}

export function ViTextField({ label, value, placeholder, variant, onChange, onCommit }) {
  const id = useId();
  const type = variant === 'obscured' ? 'password' : variant === 'number' ? 'number' : 'text';
  const common = {
    id, value: value == null ? '' : String(value), placeholder,
    onChange: (e) => onChange?.(e.target.value),
    onBlur: (e) => onCommit?.(e.target.value),
    style: FIELD,
  };
  return (
    <div data-a2ui-textfield>
      {label ? <label htmlFor={id} style={LABEL}>{label}</label> : null}
      {variant === 'longText'
        ? <textarea {...common} rows={3} />
        : <input {...common} type={type} />}
    </div>
  );
}

export function ViChoicePicker({ label, options = [], value, multiple, displayStyle, onChange }) {
  const selected = new Set((Array.isArray(value) ? value : (value == null ? [] : [value])).map(String));
  const toggle = (val) => {
    const v = String(val);
    let next;
    if (multiple) { next = new Set(selected); if (next.has(v)) next.delete(v); else next.add(v); onChange?.([...next]); }
    else { onChange?.([v]); }
  };
  const chip = displayStyle === 'chips';
  return (
    <div data-a2ui-choicepicker>
      {label ? <span style={LABEL}>{label}</span> : null}
      <div style={{ display: 'flex', flexDirection: chip ? 'row' : 'column', flexWrap: 'wrap', gap: 8 }}>
        {options.map((o, i) => {
          const val = o && typeof o === 'object' ? o.value : o;
          const lab = o && typeof o === 'object' ? (o.label ?? o.value) : o;
          const on = selected.has(String(val));
          return (
            <button key={i} type="button" onClick={() => toggle(val)} aria-pressed={on}
              style={chip
                ? { padding: '6px 12px', borderRadius: 999, border: `1px solid ${on ? 'var(--vi-accent,#111)' : 'var(--vi-hair,#E5E5EA)'}`, background: on ? 'var(--vi-accent,#111)' : 'var(--vi-surface,#fff)', color: on ? '#fff' : 'inherit', font: 'inherit', cursor: 'pointer' }
                : { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 0, font: 'inherit', cursor: 'pointer', padding: 2 }}>
              {!chip ? <span aria-hidden="true">{on ? '☑' : '☐'}</span> : null}
              <span>{lab}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ViDateTimeInput({ label, value, enableDate = true, enableTime, onChange }) {
  const id = useId();
  const type = enableTime && enableDate ? 'datetime-local' : enableTime ? 'time' : 'date';
  return (
    <div data-a2ui-datetime>
      {label ? <label htmlFor={id} style={LABEL}>{label}</label> : null}
      <input id={id} type={type} value={value == null ? '' : String(value)}
        onChange={(e) => onChange?.(e.target.value)} style={FIELD} />
    </div>
  );
}
